// F16 — How it works, rewritten as the honest sequence of getting running.
//
// What was deleted here, and why:
//
// - Four <img> tags pointing at screenshot files (products-page,
//   waiter-sales, cashier-sales, reporting-analytics) that have never existed.
//   All four resolved to the SPA's index.html, so the live page rendered four
//   broken images behind an onError handler that swapped in an emoji
//   placeholder. They now point at the real captures that ship in
//   public/screenshots.
// - The claim that a restaurant takes its first order within ten minutes of
//   signing up, with nothing to download and nothing to configure. False on
//   both halves: setup means entering a real menu, and the primary route is an
//   app installed from the App Store.
// - The same ten-minute setup claim in three further places: the step one
//   paragraph, the "01 Setup" card, and a setup-time statistic.
// - The two "Success Stories" — Creamio Café and Hay8 Restaurant — with their
//   invented quotes and invented percentages (25% faster service, 15% revenue,
//   90% staff satisfaction). The business is pre-pilot; those customers and
//   those figures do not exist. Presenting fabricated endorsements is also a
//   misleading commercial practice under the UK Consumer Protection from
//   Unfair Trading Regulations.
// - "Real-Time Analytics (Premium)" and "Quarterly Business Reviews", which
//   described plan tiers that are not sold.
// - The commented-out CTA block at the foot of the file.
//
// Section order is now: hero, the four steps at a glance, one section per
// step, what it actually takes, one closing CTA.
import React from 'react';
import { Link } from 'react-router-dom';
import LandingLayout from '../../components/landing/LandingLayout';
import { useLanguage } from '../../context/LanguageContext';
import { C, A, TYPE, SP, R, S } from '../../components/landing/tokens';
import ArchMark from '../../components/landing/ArchMark';
import StoreBadges from '../../components/landing/StoreBadges';

// auto-fit grids stack on their own below the min track, so the two-column
// and four-column sections need no media query and mirror cleanly in RTL.
const grid = (min) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
    gap: SP.xl,
});

const HowItWorksPage = () => {
    const { t } = useLanguage();

    const steps = [
        {
            number: t('01'),
            title: t('We load your menu'),
            body: t('Send it over in whatever form you have. We enter the products, prices, add-ons and categories.'),
        },
        {
            number: t('02'),
            title: t('You install the apps'),
            body: t('Aqana POS and Aqana Manager from the App Store. The back office opens in any browser.'),
        },
        {
            number: t('03'),
            title: t('You set up the floor'),
            body: t('Tables, stations and printers, mapped to the way the venue actually runs.'),
        },
        {
            number: t('04'),
            title: t('You trade'),
            body: t('Orders are counted as they happen, so the reports build themselves.'),
        },
    ];

    const menuPoints = [
        t('Products grouped into the categories your staff already use.'),
        t('Add-ons and modifiers, each priced on its own.'),
        t('Dining options for eat in, takeaway and delivery.'),
        t('Ingredients down to the recipe, if you want stock to move when a dish sells.'),
    ];

    const appPoints = [
        t('Aqana POS on the till, and on a phone for table service.'),
        t('Aqana Manager on your phone for the day so far.'),
        t('The full back office in any browser, with nothing to install.'),
    ];

    const floorPoints = [
        t('Tables and sections, named the way your waiters name them.'),
        t('Kitchen and bar stations, each with its own screen or printer.'),
        t('Staff accounts and PINs, with the permissions each role needs.'),
        t('Receipt branding, tax rates and service charge.'),
    ];

    const tradePoints = [
        t('Cash up at the end of the day and lock the totals.'),
        t('See margin by dish and takings by hour.'),
        t('Compare a day against the same day last month.'),
        t('Log expenses against the day they belong to.'),
    ];

    const effort = [
        {
            title: t('Your menu, once'),
            body: t('A spreadsheet, a PDF or a photo of the board. We do the typing.'),
        },
        {
            title: t('One setup session'),
            body: t('In the venue, with someone who knows the floor. Not a form you fill in alone.'),
        },
        {
            title: t('A walk through per station'),
            body: t('We take the till, the waiters and the kitchen through the app before your first service.'),
        },
    ];

    const MarkedItem = ({ children }) => (
        <li style={{ display: 'flex', alignItems: 'flex-start', gap: SP.md }}>
            <span style={{ marginBlockStart: 3 }}>
                <ArchMark size={15} color={C.inkFaint} />
            </span>
            <span style={{ ...TYPE.body, color: C.inkMuted }}>{children}</span>
        </li>
    );

    const framed = {
        width: '100%',
        height: 'auto',
        display: 'block',
        borderRadius: R.md,
        border: `1px solid ${C.line}`,
        background: C.surface,
    };

    const stepHeading = (label, heading) => (
        <>
            <span style={S.label}>{label}</span>
            <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>{heading}</h2>
        </>
    );

    const list = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: SP.md };

    return (
        <LandingLayout>
            {/* ---------- 1. Hero ---------- */}
            <section style={S.section()}>
                <div style={S.container}>
                    <div style={{ maxWidth: 760 }}>
                        <h1 style={{ ...TYPE.displayXl, color: C.ink, margin: 0, textWrap: 'balance' }}>
                            {t('Getting Aqana running.')}
                        </h1>
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, maxWidth: 580, marginBlock: `${SP.lg}px ${SP['2xl']}px` }}>
                            {t('Four steps sit between signing up and your first order. Here is what each one involves, and who does the work.')}
                        </p>
                        <Link to="/contact-us" style={S.btnPrimary}>{t('Get a demo')}</Link>
                    </div>
                </div>
            </section>

            {/* ---------- 2. The four steps at a glance ---------- */}
            <section style={S.section({ deep: true })}>
                <div style={S.container}>
                    <span style={S.label}>{t('THE SEQUENCE')}</span>
                    <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                        {t('Menu, apps, floor, trade.')}
                    </h2>
                    <p style={{ ...S.prose, marginBlock: `${SP.lg}px ${SP['3xl']}px` }}>
                        {t('We do the first step for you. The rest takes a setup session in the venue rather than a form filled in at a desk.')}
                    </p>

                    <div style={grid(230)}>
                        {steps.map((step) => (
                            <div key={step.title} style={{ borderBlockStart: `1px solid ${C.line}`, paddingBlockStart: SP.lg }}>
                                <span style={{ ...TYPE.label, color: C.inkFaint, display: 'block', marginBlockEnd: SP.md }}>
                                    {step.number}
                                </span>
                                <h3 style={{ ...TYPE.title, color: C.ink, margin: 0 }}>{step.title}</h3>
                                <p style={{ ...TYPE.caption, color: C.inkMuted, marginBlockStart: SP.sm }}>{step.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- 3. Step one — the menu ---------- */}
            <section style={S.section()}>
                <div style={{ ...S.container, ...grid(400), gap: SP['4xl'], alignItems: 'center' }}>
                    <div>
                        {stepHeading(t('STEP ONE'), t('We load your menu.'))}
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, marginBlock: `${SP.lg}px ${SP.xl}px` }}>
                            {t('Send us the menu in whatever form you have it — a spreadsheet, a PDF, a photo of the board. We enter every product, price and add-on so your team never faces an empty till.')}
                        </p>
                        <ul style={list}>
                            {menuPoints.map((item) => <MarkedItem key={item}>{item}</MarkedItem>)}
                        </ul>
                        <p style={{ ...TYPE.caption, color: C.inkFaint, marginBlockStart: SP.xl }}>
                            {t('You review the whole menu before anyone trades on it.')}
                        </p>
                    </div>

                    <div style={{ ...S.card, padding: SP.md }}>
                        <img
                            src="/screenshots/manager-dashboard.png"
                            alt={t('The Aqana manager portal with the menu and the day so far on screen')}
                            loading="lazy"
                            style={framed}
                        />
                    </div>
                </div>
            </section>

            {/* ---------- 4. Step two — the apps ---------- */}
            <section style={S.section({ deep: true })}>
                <div style={{ ...S.container, ...grid(400), gap: SP['4xl'], alignItems: 'center' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: SP.lg }}>
                        <div style={{ ...S.card, padding: SP.md }}>
                            <img
                                src="/screenshots/cashier-till.png"
                                alt={t('The Aqana till on an iPad with the product grid and a live cart')}
                                loading="lazy"
                                style={framed}
                            />
                        </div>
                        <div style={{ ...S.card, padding: SP.md }}>
                            <img
                                src="/screenshots/waiter-phone.png"
                                alt={t('A waiter adding items to a table order on a phone')}
                                loading="lazy"
                                style={framed}
                            />
                        </div>
                    </div>

                    <div>
                        {stepHeading(t('STEP TWO'), t('You install the apps.'))}
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, marginBlock: `${SP.lg}px ${SP.xl}px` }}>
                            {t('Aqana POS and Aqana Manager are on the App Store. Install them on the iPads and phones you already own. The Android versions are with Google for review.')}
                        </p>
                        <ul style={{ ...list, marginBlockEnd: SP['2xl'] }}>
                            {appPoints.map((item) => <MarkedItem key={item}>{item}</MarkedItem>)}
                        </ul>
                        <StoreBadges app="pos" align="start" />
                    </div>
                </div>
            </section>

            {/* ---------- 5. Step three — the floor ---------- */}
            <section style={S.section()}>
                <div style={{ ...S.container, ...grid(400), gap: SP['4xl'], alignItems: 'center' }}>
                    <div>
                        {stepHeading(t('STEP THREE'), t('You set up the floor.'))}
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, marginBlock: `${SP.lg}px ${SP.xl}px` }}>
                            {t('This is the part that needs someone who knows the venue. We sit with you and map the room: which tables exist, which station cooks what, and where each ticket prints.')}
                        </p>
                        <ul style={list}>
                            {floorPoints.map((item) => <MarkedItem key={item}>{item}</MarkedItem>)}
                        </ul>
                        <p style={{ ...TYPE.caption, color: C.inkFaint, marginBlockStart: SP.xl }}>
                            {t('Plan for a setup session in the venue, before a service rather than during one.')}
                        </p>
                    </div>

                    <div style={{ ...S.card, padding: SP.md }}>
                        <img
                            src="/screenshots/kitchen-display.png"
                            alt={t('The Aqana kitchen display with open tickets in the order they arrived')}
                            loading="lazy"
                            style={framed}
                        />
                    </div>
                </div>
            </section>

            {/* ---------- 6. Step four — trading ---------- */}
            <section style={S.section({ deep: true })}>
                <div style={{ ...S.container, ...grid(400), gap: SP['4xl'], alignItems: 'center' }}>
                    <div style={{ ...S.card, padding: SP.md }}>
                        <img
                            src="/screenshots/manager-analytics.png"
                            alt={t('The Aqana analytics view showing product performance and margin by dish')}
                            loading="lazy"
                            style={framed}
                        />
                    </div>

                    <div>
                        {stepHeading(t('STEP FOUR'), t('You trade.'))}
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, marginBlock: `${SP.lg}px ${SP.xl}px` }}>
                            {t('From the first order the numbers accumulate on their own. Nobody exports a file, and nobody types a total into a spreadsheet at midnight.')}
                        </p>
                        <ul style={list}>
                            {tradePoints.map((item) => <MarkedItem key={item}>{item}</MarkedItem>)}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ---------- 7. What it actually takes ---------- */}
            <section style={S.section()}>
                <div style={S.container}>
                    <span style={S.label}>{t('WHAT IT TAKES')}</span>
                    <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                        {t('Honest about the effort.')}
                    </h2>
                    <p style={{ ...S.prose, marginBlock: `${SP.lg}px ${SP['3xl']}px` }}>
                        {t('Putting a real menu into a real till takes a working session, not a countdown. We would rather say so now than have you find out on a Friday night.')}
                    </p>

                    <div style={{ ...grid(240), gap: SP['2xl'] }}>
                        {effort.map((item) => (
                            <div key={item.title} style={{ borderBlockStart: `1px solid ${C.line}`, paddingBlockStart: SP.lg }}>
                                <h3 style={{ ...TYPE.title, color: C.ink, margin: 0 }}>{item.title}</h3>
                                <p style={{ ...TYPE.caption, color: C.inkFaint, marginBlockStart: SP.sm }}>{item.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- 8. Closing CTA ---------- */}
            <section
                style={{
                    ...S.section(),
                    background: C.ink,
                    color: C.paper,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <ArchMark
                    size={420}
                    color={C.paper}
                    opacity={0.05}
                    style={{ position: 'absolute', insetInlineEnd: -60, insetBlockStart: -40, pointerEvents: 'none' }}
                />
                <div style={{ ...S.container, position: 'relative', textAlign: 'center' }}>
                    <h2 style={{ ...TYPE.displayL, color: C.paper, margin: 0, textWrap: 'balance' }}>
                        {t('Start with your own menu.')}
                    </h2>
                    <p style={{ ...TYPE.bodyL, color: A.paperMuted, maxWidth: 520, marginInline: 'auto', marginBlock: `${SP.lg}px ${SP['2xl']}px` }}>
                        {t('Send us your products and prices. We will set Aqana up with them and walk you through a service.')}
                    </p>
                    <Link
                        to="/contact-us"
                        style={{ ...S.btnPrimary, background: C.paper, color: C.ink, border: `1px solid ${C.paper}` }}
                    >
                        {t('Get a demo')}
                    </Link>
                </div>
            </section>
        </LandingLayout>
    );
};

export default HowItWorksPage;
